/**
 * @fileoverview Modello dati centralizzato per l'app Padel Ranking.
 * Definisce tutte le collection Firestore, i campi, i tipi e le relazioni.
 * Questo file è l'unico riferimento per il dominio applicativo: il resto
 * dell'app deve importare costanti e strutture da qui.
 */

// =============================================================================
// NOMI COLLECTION (usare queste costanti invece di stringhe inline)
// =============================================================================

export const COLLECTIONS = {
  USERS: 'users',
  GROUPS: 'groups',
  GROUP_MEMBERS: 'groupMembers',
  PERIODS: 'periods',
  MATCHES: 'matches',
  RATINGS: 'ratings',
  HALL_OF_FAME: 'hallOfFame',
};

// =============================================================================
// USERS
// =============================================================================

/**
 * Document ID = Firebase Auth UID.
 *
 * @typedef {Object} User
 * @property {string} email - Email dell'utente (coerente con Firebase Auth)
 * @property {string} [displayName] - Nome visualizzato
 * @property {import('firebase/firestore').Timestamp} [createdAt] - Data di creazione profilo
 * @property {import('firebase/firestore').Timestamp} [updatedAt] - Ultimo aggiornamento
 */
export const USERS_FIELDS = {
  EMAIL: 'email',
  DISPLAY_NAME: 'displayName',
  CREATED_AT: 'createdAt',
  UPDATED_AT: 'updatedAt',
};

// =============================================================================
// GROUPS
// =============================================================================

/**
 * Gruppo di giocatori (es. circolo, lega, cerchia di amici).
 *
 * @typedef {Object} Group
 * @property {string} name - Nome del gruppo
 * @property {string} [nameLower] - Nome in minuscolo per ricerche case-insensitive
 * @property {string} [description] - Descrizione opzionale
 * @property {string} ownerId - UID del proprietario (riferimento a users)
 * @property {import('firebase/firestore').Timestamp} [createdAt]
 * @property {import('firebase/firestore').Timestamp} [updatedAt]
 */
export const GROUPS_FIELDS = {
  NAME: 'name',
  NAME_LOWER: 'nameLower',
  DESCRIPTION: 'description',
  OWNER_ID: 'ownerId',
  CREATED_AT: 'createdAt',
  UPDATED_AT: 'updatedAt',
};

// =============================================================================
// GROUP MEMBERS
// =============================================================================

/**
 * Iscrizione di un utente a un gruppo (N:N tra users e groups).
 *
 * @typedef {Object} GroupMember
 * @property {string} groupId - ID documento in groups
 * @property {string} userId - UID utente (riferimento a users)
 * @property {'owner'|'admin'|'member'} [role] - Ruolo nel gruppo (default: 'member')
 * @property {'active'|'invited'|'pending_request'|'pending_merge'|'rejected'} [status] - Stato dell'iscrizione
 * @property {boolean} [isGuest] - Se true, membro ospite
 * @property {string} [displayName] - Nome mostrato in questo gruppo (override)
 * @property {import('firebase/firestore').Timestamp} [joinedAt]
 * @property {import('firebase/firestore').Timestamp} [updatedAt]
 */
export const GROUP_MEMBERS_FIELDS = {
  GROUP_ID: 'groupId',
  USER_ID: 'userId',
  ROLE: 'role',
  STATUS: 'status',
  IS_GUEST: 'isGuest',
  GUEST_NICKNAME: 'guestNickname',
  DISPLAY_NAME: 'displayName',
  JOINED_AT: 'joinedAt',
  UPDATED_AT: 'updatedAt',
};

/** @type {Readonly<Record<string, string>>} */
export const GROUP_MEMBER_ROLES = {
  OWNER: 'owner',
  ADMIN: 'admin',
  MEMBER: 'member',
};

export const GROUP_MEMBER_STATUS = {
  ACTIVE: 'active',
  INVITED: 'invited',
  PENDING_REQUEST: 'pending_request',
  PENDING_MERGE: 'pending_merge',
  REJECTED: 'rejected',
};

// =============================================================================
// PERIODS
// =============================================================================

/**
 * Periodo temporale di riferimento per partite e classifiche (es. stagione, mese).
 *
 * @typedef {Object} Period
 * @property {string} groupId - ID del gruppo (riferimento a groups)
 * @property {string} name - Nome del periodo (es. "Stagione 2024", "Marzo 2024")
 * @property {import('firebase/firestore').Timestamp} startDate - Inizio periodo
 * @property {import('firebase/firestore').Timestamp} endDate - Fine periodo
 * @property {string} [ratingMode] - Modalità di calcolo ELO della stagione (vedi RATING_MODES)
 * @property {string} [ratingPreset] - Preset pesi per i formati di gioco (vedi RATING_PRESETS)
 * @property {import('firebase/firestore').Timestamp} [createdAt]
 * @property {import('firebase/firestore').Timestamp} [updatedAt]
 */
export const PERIODS_FIELDS = {
  GROUP_ID: 'groupId',
  NAME: 'name',
  START_DATE: 'startDate',
  END_DATE: 'endDate',
  RATING_MODE: 'ratingMode',
  RATING_PRESET: 'ratingPreset',
  CREATED_AT: 'createdAt',
  UPDATED_AT: 'updatedAt',
};

/** Modalità di calcolo ELO per la stagione */
export const RATING_MODES = {
  RESULT_ONLY: 'RESULT_ONLY',
  EXPECTED_PERFORMANCE: 'EXPECTED_PERFORMANCE',
};

/** Preset pesi per i formati di gioco */
export const RATING_PRESETS = {
  CLASSIC: 'CLASSIC',
  BALANCED: 'BALANCED',
  TOTAL: 'TOTAL',
};

/**
 * Moltiplicatori del delta ELO per formato, per ciascun preset.
 * Il peso si applica SOLO al delta ELO, non alle statistiche (matchesPlayed, wins, losses).
 */
export const RATING_PRESET_WEIGHTS = {
  CLASSIC: { MATCH: 1.0, ROTATION: 0.0, COMPETITION: 0.5 },
  BALANCED: { MATCH: 1.0, ROTATION: 0.4, COMPETITION: 0.7 },
  TOTAL: { MATCH: 1.0, ROTATION: 1.0, COMPETITION: 1.0 },
};

/** Opzioni per il rating iniziale alla creazione di una nuova stagione */
export const SEASON_START_OPTIONS = {
  /** Tutti i giocatori ripartono da RATING_BASE (1000) */
  RESET: 'RESET',
  /** Il rating iniziale è il rating finale della stagione precedente */
  CARRY_OVER: 'CARRY_OVER',
};

// =============================================================================
// MATCHES
// =============================================================================

/**
 * Una partita giocata (due coppie, punteggio).
 * Può essere Classica, King o Super King; i set sono in array sets.
 *
 * @typedef {Object} Match
 * @property {string} groupId - ID del gruppo (riferimento a groups)
 * @property {string} [periodId] - ID periodo (riferimento a periods)
 * @property {string} type - Tipo legacy: classic, king, super_king
 * @property {string} [gameFormat] - Formato logico: MATCH, ROTATION, COMPETITION (vedi GAME_FORMATS)
 * @property {string} [ratingImpact] - Impatto sul ranking (vedi RATING_IMPACT)
 * @property {MatchParticipant[]} [participants] - Snapshot ELO per partecipante all'approvazione definitiva
 * @property {string} status - pending_approval, approved, rejected
 * @property {string} createdBy - UID chi ha inserito (riferimento a users)
 * @property {import('firebase/firestore').Timestamp} [createdAt]
 * @property {Array<{ userId: string, approvedAt: import('firebase/firestore').Timestamp }>} [approvals]
 * @property {Array<{ field?: string, team1: object, team2: object, score1: number, score2: number }>} sets - Array di set
 */
export const MATCHES_FIELDS = {
  GROUP_ID: 'groupId',
  PERIOD_ID: 'periodId',
  TYPE: 'type',
  GAME_FORMAT: 'gameFormat',
  RATING_IMPACT: 'ratingImpact',
  PARTICIPANTS: 'participants',
  STATUS: 'status',
  CREATED_BY: 'createdBy',
  CREATED_AT: 'createdAt',
  APPROVALS: 'approvals',
  SETS: 'sets',
  TEAM1: 'team1',
  TEAM2: 'team2',
  SCORE1: 'score1',
  SCORE2: 'score2',
  PLAYED_AT: 'playedAt',
  UPDATED_AT: 'updatedAt',
};

/** Alias per coerenza con documentazione */
export const MATCH_FIELDS = MATCHES_FIELDS;

/** @type {Readonly<Record<string, string>>} */
export const MATCH_STATUS = {
  PENDING_APPROVAL: 'pending_approval',
  APPROVED: 'approved',
  REJECTED: 'rejected',
};

/** @type {Readonly<Record<string, string>>} */
export const MATCH_TYPES = {
  CLASSIC: 'classic',
  KING: 'king',
  SUPER_KING: 'super_king',
};

/**
 * Formato logico della partita.
 * Mappa dalle label UI ai valori interni:
 *   Classic Match      → MATCH
 *   King of the Court  → ROTATION
 *   American Tournament / Super King → COMPETITION
 */
export const GAME_FORMATS = {
  MATCH: 'MATCH',
  ROTATION: 'ROTATION',
  COMPETITION: 'COMPETITION',
};

/**
 * Mappa legacy: dal vecchio campo `type` al nuovo `gameFormat`.
 * Usata durante la migrazione dati e per retrocompatibilità.
 */
export const LEGACY_TYPE_TO_GAME_FORMAT = {
  classic: 'MATCH',
  king: 'ROTATION',
  super_king: 'COMPETITION',
};

/** Impatto del match sul ranking della stagione */
export const RATING_IMPACT = {
  /** Aggiorna ELO e statistiche */
  OFFICIAL: 'OFFICIAL',
  /** Salva nello storico ma non aggiorna ELO */
  EXCLUDED: 'EXCLUDED',
};

/**
 * Un set della partita (coppie e punteggio).
 * @typedef {Object} MatchSet
 * @property {string} [field] - Per Super King: gold_king, silver_king, gold_final, silver_final
 * @property {{ player1: string, player2: string }} team1
 * @property {{ player1: string, player2: string }} team2
 * @property {number} score1
 * @property {number} score2
 */

/**
 * Snapshot ELO di un partecipante al momento dell'approvazione definitiva del match.
 * Scritto su match.participants al momento dell'approvazione.
 *
 * @typedef {Object} MatchParticipant
 * @property {string} memberId - ID documento groupMembers
 * @property {string|null} [userId] - UID utente (null per guest)
 * @property {number} ratingBefore - Rating ELO prima del match
 * @property {number|null} ratingAfter - Rating ELO dopo il match (null se ratingImpact = EXCLUDED)
 * @property {number|null} delta - Variazione ELO (null se ratingImpact = EXCLUDED)
 */
export const MATCH_PARTICIPANT_FIELDS = {
  MEMBER_ID: 'memberId',
  USER_ID: 'userId',
  RATING_BEFORE: 'ratingBefore',
  RATING_AFTER: 'ratingAfter',
  DELTA: 'delta',
};

export const SET_FIELDS = {
  FIELD: 'field',
  TEAM1: 'team1',
  TEAM2: 'team2',
  SCORE1: 'score1',
  SCORE2: 'score2',
};

/** Valori di field per Super King */
export const SET_FIELD_VALUES = {
  GOLD_KING: 'gold_king',
  SILVER_KING: 'silver_king',
  GOLD_FINAL: 'gold_final',
  SILVER_FINAL: 'silver_final',
};

/** Sottocampi per team1/team2 */
export const MATCH_TEAM_FIELDS = {
  PLAYER1: 'player1',
  PLAYER2: 'player2',
};

// =============================================================================
// RATINGS
// =============================================================================

/**
 * Punteggio/classifica di un giocatore in un gruppo (e opzionalmente in un periodo).
 *
 * @typedef {Object} Rating
 * @property {string} groupId - ID del gruppo (riferimento a groups)
 * @property {string} [periodId] - ID periodo se la classifica è per periodo (riferimento a periods)
 * @property {string} userId - UID del giocatore (riferimento a users)
 * @property {number} rating - Rating ELO corrente
 * @property {number} matchesPlayed - Numero di match giocati (conteggio match, non set)
 * @property {number} [wins] - Vittorie
 * @property {number} [losses] - Sconfitte
 * @property {boolean} [isActive] - Se false o assente, il membro è uscito dal gruppo; in classifica si considerano solo rating con isActive !== false
 * @property {import('firebase/firestore').Timestamp} [updatedAt] - Ultimo ricalcolo
 */
export const RATINGS_FIELDS = {
  GROUP_ID: 'groupId',
  PERIOD_ID: 'periodId',
  USER_ID: 'userId',
  /** ID documento groupMembers quando il rating è di un ospite */
  MEMBER_ID: 'memberId',
  RATING: 'rating',
  MATCHES_PLAYED: 'matchesPlayed',
  WINS: 'wins',
  LOSSES: 'losses',
  /** Se false, il membro è uscito dal gruppo; in classifica si considerano solo rating attivi */
  IS_ACTIVE: 'isActive',
  UPDATED_AT: 'updatedAt',
};

/** Parametri globali del sistema ELO */
export const ELO_CONFIG = {
  /** Rating di partenza per ogni giocatore a inizio stagione */
  RATING_BASE: 1000,
  /** Fattore K per giocatori con meno di K_THRESHOLD match giocati nella stagione */
  K_PROVISIONAL: 40,
  /** Fattore K standard per giocatori con K_THRESHOLD o più match giocati */
  K_STANDARD: 20,
  /** Soglia match dopo cui si passa da K_PROVISIONAL a K_STANDARD */
  K_THRESHOLD: 10,
  /** Cap massimo di punti guadagnabili per match */
  MAX_DELTA: 32,
  /** Cap minimo (simmetrico) di punti perdibili per match */
  MIN_DELTA: -32,
  /** Coefficiente del marginMultiplier: determina quanto una vittoria netta vale più di una tirata */
  MARGIN_MULTIPLIER_COEFF: 0.5,
};

// =============================================================================
// HALL OF FAME
// =============================================================================

/**
 * Record storico / achievement (es. campione del mese, miglior miglioramento).
 *
 * @typedef {Object} HallOfFameEntry
 * @property {string} groupId - ID del gruppo (riferimento a groups)
 * @property {string} [periodId] - ID periodo (riferimento a periods)
 * @property {string} [userId] - UID del giocatore premiato (riferimento a users)
 * @property {string} type - Tipo di riconoscimento (es. 'champion', 'most_improved', 'fair_play')
 * @property {Object} [data] - Dati aggiuntivi (flessibile per tipo)
 * @property {import('firebase/firestore').Timestamp} [createdAt]
 */
export const HALL_OF_FAME_FIELDS = {
  GROUP_ID: 'groupId',
  PERIOD_ID: 'periodId',
  USER_ID: 'userId',
  TYPE: 'type',
  DATA: 'data',
  /** Array di classifiche finali alla chiusura del periodo: [{ position, memberId, finalRating, matchesPlayed, matchesWon, winRate, gamesWon }] */
  RANKINGS: 'rankings',
  CREATED_AT: 'createdAt',
};

/** @type {Readonly<Record<string, string>>} */
export const HALL_OF_FAME_TYPES = {
  CHAMPION: 'champion',
  MOST_IMPROVED: 'most_improved',
  FAIR_PLAY: 'fair_play',
};

// =============================================================================
// RELAZIONI (riferimento rapido)
// =============================================================================
//
// users (1) ──< groupMembers >── (N) groups
// groups (1) ──< periods
// groups (1) ──< matches (opzionale: participants = snapshot ELO all'approvazione)
// groups (1) ──< ratings
// groups (1) ──< hallOfFame
// periods (1) ──< matches (opzionale, via periodId)
// periods (1) ──< ratings (opzionale, via periodId)
// periods (1) ──< hallOfFame (opzionale, via periodId)
// users (1) ──< ratings (userId)
// users (1) ──< hallOfFame (userId)
//
